import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "./ui/button";
import { Clock, Sparkles } from "lucide-react";

interface TrialCountdownProps {
  trialEndsAt?: Date | string | null;
}

export function TrialCountdown({ trialEndsAt }: TrialCountdownProps) {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [isExpired, setIsExpired] = useState(false);

  useEffect(() => {
    if (!trialEndsAt) {
      setIsExpired(true);
      return;
    }

    const calculateTimeLeft = () => {
      const trialEnd = new Date(trialEndsAt);
      const now = new Date();
      const difference = trialEnd.getTime() - now.getTime();

      if (difference <= 0) {
        setIsExpired(true);
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        return;
      }

      const days = Math.floor(difference / (1000 * 60 * 60 * 24));
      const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((difference % (1000 * 60)) / 1000);

      setTimeLeft({ days, hours, minutes, seconds });
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);

    return () => clearInterval(timer);
  }, [trialEndsAt]);

  const formatTime = (value: number) => value.toString().padStart(2, "0");

  return (
    <div className="flex items-center gap-3">
      <div className="flex items-center gap-2 px-3 py-1.5 bg-primary/10 rounded-lg border border-primary/20">
        <Clock className="w-4 h-4 text-primary" />
        <div className="flex items-center gap-1">
          {isExpired ? (
            <span className="text-sm font-medium text-destructive">Trial expired</span>
          ) : (
            <>
              <span className="text-xs text-muted-foreground">Free trial:</span>
              <span className="text-sm font-mono font-semibold text-foreground">
                {timeLeft.days}d {formatTime(timeLeft.hours)}:{formatTime(timeLeft.minutes)}:{formatTime(timeLeft.seconds)}
              </span>
            </>
          )}
        </div>
      </div>
      
      <Link to="/pricing">
        <Button size="sm" variant="default" className="gap-1.5">
          <Sparkles className="w-3.5 h-3.5" />
          <span className="hidden sm:inline">Upgrade Plan</span>
          <span className="sm:hidden">Upgrade</span>
        </Button>
      </Link>
    </div>
  );
}